import { Injectable } from "@angular/core";
import {
  EMPTY,
  catchError,
  exhaustMap,
  from,
  map,
  mergeMap,
  of,
  switchMap
} from "rxjs";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as settingsActions from "./settings.action";
import * as errorActions from "../../core/error/ngrx/error.actions";
import { DataService } from "src/app/supabase/services/data.service";
import {
  loadEducationCategories
} from "./settings.action";
import { ToastController } from "@ionic/angular";
import { AuthService } from "src/app/supabase/services/auth.service";

@Injectable()
export class SettingEffects {
  constructor(
    private actions$: Actions,
    private dataService: DataService,
    private toastController: ToastController,
    private authService: AuthService
  ) {}

  loadEducationCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEducationCategories),
      mergeMap(() =>
        from(this.dataService.getEducationCategories()).pipe(
          map((educationCategories) =>
            settingsActions.loadEducationCategoriesSuccess({
              educationCategories,
            })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskategorien konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadEducationTitles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadEducationTitles),
      mergeMap(() =>
        from(this.dataService.getEducationTitles()).pipe(
          map((educationTitles) =>
            settingsActions.loadEducationTitlesSuccess({ educationTitles })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungstitel konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadEducationBodies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadEducationBodies),
      mergeMap(() =>
        from(this.dataService.getEducationBodies()).pipe(
          map((educationBodies) =>
            settingsActions.loadEducationBodiesSuccess({ educationBodies })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskörper konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadEducationTitlesForCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadEducationTitlesForCategory),
      mergeMap(({ educationCategoryId }) =>
        from(this.dataService.getEducationTitles(educationCategoryId)).pipe(
          map((educationTitles) =>
            settingsActions.loadEducationTitlesForCategorySuccess({
              educationTitles,
            })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungstitel konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadEducationBodiesForTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadEducationBodiesForTitle),
      mergeMap(({ educationTitleId }) =>
        from(this.dataService.getEducationBodies(educationTitleId)).pipe(
          map((educationBodies) =>
            settingsActions.loadEducationBodiesForTitleSuccess({
              educationBodies,
            })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskörper konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  updateEducationTitles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.updateEducationTitles),
      mergeMap(({ educationTitles }) =>
        from(this.dataService.updateEducationTitles(educationTitles)).pipe(
          map((educationTitles) =>
            settingsActions.updateEducationTitlesSuccess({ educationTitles })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungstitel konnten nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  updateEducationBodies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.updateEducationBodies),
      mergeMap(({ educationBodies }) =>
        from(this.dataService.updateEducationBodies(educationBodies)).pipe(
          map((educationBodies) =>
            settingsActions.updateEducationBodiesSuccess({ educationBodies })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskörper konnten nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  addEducationCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.addEducationCategory),
      mergeMap(({ educationCategory }) =>
        from(this.dataService.addEducationCategory(educationCategory)).pipe(
          map((educationCategory) =>
            settingsActions.addEducationCategorySuccess({ educationCategory })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskategorie konnte nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  updateEducationCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.updateEducationCategory),
      mergeMap(({ educationCategory }) =>
        from(this.dataService.updateEducationCategory(educationCategory)).pipe(
          map((educationCategory) =>
            settingsActions.updateEducationCategorySuccess({ educationCategory })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskategorie konnte nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  deleteEducationCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.deleteEducationCategory),
      mergeMap(({ educationCategory }) =>
        // Show the toast and switch to a new observable representing user response
        from(this.showUndoToast()).pipe(
          switchMap((toastAction) => {
            if (toastAction.role === 'cancel') {
              // User clicked Undo, dispatch an action to handle undo logic
              return of(settingsActions.undoDeleteEducationCategory({educationCategory}));
            } else {
              // User didn't click Undo, proceed with deletion
              return from(this.dataService.deleteEducationCategory(educationCategory)).pipe(
                map((educationCategory) => settingsActions.deleteEducationCategorySuccess({educationCategory})),
                catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskategorie konnte nicht gelöscht werden.", exception: error})))
              );
            }
          })
        )
      )
    )
  );

  updateEducationTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.updateEducationTitle),
      mergeMap(({ educationTitle }) =>
        from(this.dataService.updateEducationTitle(educationTitle)).pipe(
          map((educationTitle) =>
            settingsActions.updateEducationTitleSuccess({ educationTitle })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungstitel konnten nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  deleteEducationTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.deleteEducationTitle),
      mergeMap(({ educationTitle }) =>
        // Show the toast and switch to a new observable representing user response
        from(this.showUndoToast()).pipe(
          switchMap((toastAction) => {
            if (toastAction.role === 'cancel') {
              // User clicked Undo, dispatch an action to handle undo logic
              return of(settingsActions.undoDeleteEducationTitle({educationTitle}));
            } else {
              // User didn't click Undo, proceed with deletion
              return from(this.dataService.deleteEducationTitle(educationTitle)).pipe(
                map((educationTitle) => settingsActions.deleteEducationTitleSuccess({educationTitle})),
                catchError((error) => of(errorActions.handleError({error: "Die Ausbildungstitel konnte nicht gelöscht werden.", exception: error})))
              );
            }
          })
        )
      )
    )
  );

  updateEducationBody$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.updateEducationBody),
      mergeMap(({ educationBody }) =>
        from(this.dataService.updateEducationBody(educationBody)).pipe(
          map((educationBody) =>
            settingsActions.updateEducationBodySuccess({ educationBody })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskörper konnten nicht aktualisiert werden.", exception: error})))
        )
      )
    )
  );

  deleteEducationBody$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.deleteEducationBody),
      mergeMap(({ educationBody }) =>
        // Show the toast and switch to a new observable representing user response
        from(this.showUndoToast()).pipe(
          switchMap((toastAction) => {
            if (toastAction.role === 'cancel') {
              // User clicked Undo, dispatch an action to handle undo logic
              return of(settingsActions.undoDeleteEducationBody({educationBody}));
            } else {
              // User didn't click Undo, proceed with deletion
              return from(this.dataService.deleteEducationBody(educationBody)).pipe(
                map((educationBody) => settingsActions.deleteEducationBodySuccess({educationBody})),
                catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskörper konnte nicht gelöscht werden.", exception: error})))
              );
            }
          })
        )
      )
    )
  );

  addEducationTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.addEducationTitle),
      mergeMap(({ educationTitle }) =>
        from(this.dataService.addEducationTitle(educationTitle)).pipe(
          map((educationTitle) =>
            settingsActions.addEducationTitleSuccess({ educationTitle })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungstitel konnte nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  addEducationBody$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.addEducationBody),
      mergeMap(({ educationBody }) =>
        from(this.dataService.addEducationBody(educationBody)).pipe(
          map((educationBody) =>
            settingsActions.addEducationBodySuccess({ educationBody })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Ausbildungskörper konnte nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  addLessonRatings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.addLessonRatings),
      exhaustMap(({ lessonRatings }) =>
        from(this.dataService.addLessonRatings(lessonRatings)).pipe(
          map((lessonRatings) =>
            settingsActions.addLessonRatingsSuccess({ lessonRatings })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Bewertungen konnten nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  loadLessonRatings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadLessonRatings),
      mergeMap(() =>
        from(this.dataService.getLessonRatings()).pipe(
          map((lessonRatings) =>
            settingsActions.loadLessonRatingsSuccess({ lessonRatings })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Bewertungen konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadLessonRatingsForlesson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadLessonRatingsForLesson),
      mergeMap(({ lessonId }) =>
        from(this.dataService.getLessonRatingsForLesson(lessonId)).pipe(
          map((lessonRatings) =>
            settingsActions.loadLessonRatingsForLessonSuccess({
              lessonRatings,
            })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Bewertungen konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  loadLessonRatingsForStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadLessonRatingsForStudent),
      mergeMap(({ studentId }) =>
        from(this.dataService.getLessonRatingsForStudent(studentId)).pipe(
          map((lessonRatings) =>
            settingsActions.loadLessonRatingsForStudentSuccess({
              lessonRatings,
            })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Bewertungen konnten nicht geladen werden.", showToast: false, exception: error})))
        )
      )
    )
  );

  removeLessonRatings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.removeLessonRatings),
      mergeMap(({ lessonRatings }) =>
        from(this.dataService.deleteLessonRatings(lessonRatings)).pipe(
          map((lessonRatings) =>
            settingsActions.removeLessonRatingsSuccess({ lessonRatings })
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Bewertungen konnten nicht gelöscht werden.", exception: error})))
        )
      )
    )
  );

  disConnectGoogleCalendar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.disconnectGoogleCalendar),
      mergeMap(({ customerId }) =>
      from(this.dataService.disconnectGoogleCalendar(customerId)).pipe(
          map((customer) =>
            settingsActions.disconnectGoogleCalendarSuccess({ customer }),
            this.authService.unlinkIdentityProvider("google"),
          ),
          catchError((error) => of(errorActions.handleError({error: "Die Google Kalenderverbindung konnte nicht getrennt werden.", exception: error})))
        )
    )
  ));

  disconnectLocalCalendar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.disconnectLocalCalendar),
      mergeMap(({ customerId }) =>
      from(this.dataService.disconnectLocalCalendar(customerId)).pipe(
            map((customer) =>
              settingsActions.disconnectLocalCalendarSuccess({ customer })
            ),
            catchError((error) => of(errorActions.handleError({error: "Die lokale Kalenderverbindung konnte nicht getrennt werden.", exception: error})))
          )
      )
  ));

  loadServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadServices),
      mergeMap(() =>
      from(this.dataService.loadServices()).pipe(
        map((services) =>
          settingsActions.loadServicesSuccess({ services }),
        ),
        catchError((error) => of(errorActions.handleError({error: "Die Dienste konnten nicht geladen werden.", showToast: false, exception: error})))
      )
      )
    )
  );

  loadSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.loadSubscriptions),
      mergeMap(() =>
      from(this.dataService.loadSubscriptions()).pipe(
        map((subscriptions) =>
          settingsActions.loadSubscriptionsSuccess({ subscriptions }),
        ),
        catchError((error) => of(errorActions.handleError({error: "Die Abonnements konnten nicht geladen werden.", showToast: false, exception: error})))
      )
      )
    )
  );

  upsertService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.upsertService),
      mergeMap(({ service }) =>
        from(this.dataService.upsertService(service)).pipe(
          map((service) =>
            settingsActions.upsertServiceSuccess({ service })
          ),
          catchError((error) => of(errorActions.handleError({error: "Der Dienst konnte nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  upsertSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.upsertSubscription),
      mergeMap(({ subscription }) =>
        from(this.dataService.upsertSubscription(subscription)).pipe(
          map((subscription) =>
            settingsActions.upsertSubscriptionSuccess({ subscription })
          ),
          catchError((error) => of(errorActions.handleError({error: "Das Abonnement konnte nicht erstellt werden.", exception: error})))
        )
      )
    )
  );

  deleteService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.deleteService),
      mergeMap(({ serviceId }) =>
        from(this.dataService.deleteService(serviceId)).pipe(
          map((service) =>
            settingsActions.deleteServiceSuccess({ serviceId })
          ),
          catchError((error) => of(errorActions.handleError({error: "Der Dienst konnte nicht gelöscht werden.", exception: error})))
        )
      )
    )
  );

  deleteSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.deleteSubscription),
      mergeMap(({ subscriptionId }) =>
        from(this.dataService.deleteSubscription(subscriptionId)).pipe(
          map((subscription) =>
            settingsActions.deleteSubscriptionSuccess({ subscriptionId })
          ),
          catchError((error) => of(errorActions.handleError({error: "Das Abonnement konnte nicht gelöscht werden.", exception: error})))
        )
      )
    )
  );

  private showUndoToast() {
    return this.toastController.create({
      message: 'Der Ausbildungseintrag wurde gelöscht',
      position: 'bottom',
      duration: 5000,
      buttons: [
        {
          text: 'Rückgängig',
          role: 'cancel',
          handler: () => true // Return true to indicate that the user clicked Undo
        }
      ]
    }).then(toast => {
      toast.present();
      // Return a promise that resolves when the toast is dismissed
      return toast.onDidDismiss();
    });
  }
}
